import { z } from "zod";
import { AccusationFieldV0, ArtifactV0, CharacterV0 } from "../case-v0";
import { CarrierRefV1, FactEdgeV1, FactV1, RedHerringV1 } from "./facts";
import { ScriptedEventV1, UnlockRuleV1 } from "./unlocks";

/**
 * CaseV1 — la CDL v1 (F1-T01, ontología B22).
 *
 * Conserva la capa de PRESENTACIÓN de CaseV0 (artefactos con content/overlays,
 * cast, acusación hasheada) y le agrega la capa LÓGICA: grafo de hechos,
 * desbloqueos, hitos, eventos scriptados, herrings declarados y hints.
 *
 * En F1 un "caso" es una instancia única escrita a mano CON lógica; la
 * separación núcleo/instancia (f(core, worldPack, seed)) llega con F2-T06.
 * El schema es permisivo con grafos vacíos (fixtures de test); la riqueza la
 * exige el validador de authoring (`pnpm validate:case`), no el parser.
 */

export const ArtifactV1 = ArtifactV0.extend({
  /** Arranca oculto: solo lo revela una regla de desbloqueo (F1-T03) */
  initiallyLocked: z.boolean().default(false),
});

/** Hito intermedio (jugabilidad §4): mini-acusación con sello */
export const MilestoneV1 = z.object({
  id: z.string().min(1),
  question: z.string().min(1),
  fields: z.array(AccusationFieldV0).min(1),
  /** Mismos hashes salados que la acusación (C14/G-23); comparte el salt del caso */
  solutionHashes: z.record(z.string(), z.string().regex(/^[0-9a-f]{64}$/)),
});

/** Hint de M. por nivel (jugabilidad §8); nivel 0 es la notificación ambiental */
export const HintV1 = z.object({
  id: z.string().min(1),
  level: z.number().int().min(0).max(3),
  text: z.string().min(1),
  /** A qué atasco responde (milestoneId); sin él, aplica al caso entero */
  about: z.string().optional(),
});

export const CaseV1 = z.object({
  schemaVersion: z.literal("1.0.0"),
  id: z.string().min(1),
  title: z.string().min(1),
  /** Reloj diegético de la taskbar (etiqueta fija hasta el reloj vivo de F1-T03) */
  clockLabel: z.string().optional(),
  /** Módulos que el OS instala en el boot del caso (F1-T07) */
  requiredModules: z.array(z.string()).default([]),
  briefing: z.object({
    letter: z.string().min(1),
    openQuestions: z.array(z.string()),
  }),
  cast: z.array(CharacterV0).min(3),
  artifacts: z.array(ArtifactV1).min(10),

  // ── Capa lógica (el grafo B22) ──
  facts: z.array(FactV1).default([]),
  edges: z.array(FactEdgeV1).default([]),
  carriers: z.array(CarrierRefV1).default([]),
  redHerrings: z.array(RedHerringV1).default([]),
  unlocks: z.array(UnlockRuleV1).default([]),
  milestones: z.array(MilestoneV1).default([]),
  scriptedEvents: z.array(ScriptedEventV1).default([]),
  hints: z.array(HintV1).default([]),

  accusation: z.object({
    fields: z.array(AccusationFieldV0).min(1),
    motives: z.array(z.string()).default([]),
    /** fieldId → sha256(salt|respuesta normalizada). NUNCA la solución en claro (G-23) */
    solutionHashes: z.record(z.string(), z.string().regex(/^[0-9a-f]{64}$/)),
    salt: z.string().min(1),
    postMortem: z.string().min(1),
    /** FactIds que sustentan la acusación — ancla del validador de alcanzabilidad lógica */
    provenBy: z.array(z.string().regex(/^F\d+$/)).default([]),
  }),
});

export type ArtifactV1Data = z.infer<typeof ArtifactV1>;
export type MilestoneV1Data = z.infer<typeof MilestoneV1>;
export type HintV1Data = z.infer<typeof HintV1>;
export type CaseV1Data = z.infer<typeof CaseV1>;
